import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import AgentSelect from "../components/common/AgentSelect";
import Modal from "../components/common/Modal";
import PageLoader from "../components/common/PageLoader";
import StatusBadge from "../components/common/StatusBadge";
import Table from "../components/common/Table";
import { fetchCurrentAgent } from "../lib/agentAuth";
import { canUpdateKyc, roleFromProfile } from "../lib/agencyAccess";
import { getKycRequirement, KYC_DOCUMENT_TYPES, kycTypeLabel, validateKycFile } from "../lib/agentOnboarding";
import { agentApi } from "../lib/apiClient";
import { showApiError, showError, showErrorAction, showSuccessAction } from "../lib/alerts";
import { downloadBlob, formatDate } from "../lib/catalog";
import { fetchCities, fetchCountries, fetchStates, findLocationLabel } from "../lib/locations";

const KYC_TYPES = KYC_DOCUMENT_TYPES;
const KYC_ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

function DetailItem({ label, value }) {
  return (
    <div className="agent-kyc-detail">
      <div className="agent-kyc-detail__label">{label}</div>
      <div className="agent-kyc-detail__value">{value || "Not set"}</div>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <section className="agent-kyc-block">
      <h6 className="agent-kyc-block__title">{title}</h6>
      <div className="row g-3">{children}</div>
    </section>
  );
}

function typeLabel(value) {
  return kycTypeLabel(value);
}

function bannerTypeLabel(value) {
  const type = String(value || "").trim().toLowerCase();
  if (type === "pan_card") return "PAN";
  if (type === "gst_certificate") return "GST";
  return typeLabel(value);
}

function isImageFile(mimeType, fileName = "") {
  return String(mimeType || "").startsWith("image/") || /\.(jpe?g|png|gif|webp|bmp)$/i.test(fileName);
}

function statusKey(value) {
  return String(value || "").toLowerCase().replace(/[\s-]+/g, "_");
}

function isPendingReview(value) {
  return ["submitted", "under_review"].includes(statusKey(value));
}

function isRejectedReview(value) {
  return ["rejected", "action_required"].includes(statusKey(value));
}

function reviewStatusLabel(value) {
  const status = statusKey(value);
  if (isPendingReview(status)) return "Pending Verification";
  if (status === "action_required") return "Action required";
  return String(value || "unknown").replace(/_/g, " ");
}

function withCurrentFlags(documents) {
  const latestByType = {};
  documents.forEach((doc) => {
    const type = doc.document_type;
    if (!latestByType[type] || Number(doc.kyc_document_id) > Number(latestByType[type].kyc_document_id)) {
      latestByType[type] = doc;
    }
  });
  return documents.map((doc) => ({
    ...doc,
    is_current: doc.is_current === true || doc.is_current === false
      ? Boolean(doc.is_current)
      : latestByType[doc.document_type]?.kyc_document_id === doc.kyc_document_id,
  }));
}

function verificationCopy(profile, documents) {
  const verification = statusKey(profile?.verification_status);
  const current = documents.filter((doc) => doc.is_current);
  const pending = current.filter((doc) => isPendingReview(doc.review_status));
  const rejected = current.filter((doc) => isRejectedReview(doc.review_status));

  if (verification === "verified") {
    return {
      badge: "Verified",
      body: "KYC is verified. Use Replace if a document needs to be updated. Changes are reviewed before verification is restored.",
    };
  }
  if (pending.length === 1) {
    return {
      badge: "Pending Verification",
      body: `Your updated ${bannerTypeLabel(pending[0].document_type)} document is awaiting verification.`,
    };
  }
  if (pending.length > 1) {
    return {
      badge: "Pending Verification",
      body: "Your updated KYC documents are awaiting verification.",
    };
  }
  if (rejected.length) {
    const reason = rejected[0].review_notes;
    return {
      badge: verification === "rejected" ? "Rejected" : "Pending Verification",
      body: reason
        ? `A reviewer rejected your ${typeLabel(rejected[0].document_type)} document: ${reason}`
        : `A reviewer rejected your ${typeLabel(rejected[0].document_type)} document. Use Resubmit to upload a corrected file.`,
    };
  }
  return {
    badge: "Pending Verification",
    body: "Operations reviews uploaded files. Verification is set automatically after required documents are approved.",
  };
}

export default function AgencyDetailPage() {
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentType, setDocumentType] = useState("pan_card");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [replaceDoc, setReplaceDoc] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replaceReason, setReplaceReason] = useState("");
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const previewUrlRef = useRef("");
  const replaceInputRef = useRef(null);
  const uploadInputRef = useRef(null);

  const taggedDocuments = useMemo(() => withCurrentFlags(documents), [documents]);
  const isVerified = statusKey(profile?.verification_status) === "verified";
  const copy = verificationCopy(profile, taggedDocuments);
  const requirement = getKycRequirement(profile?.legal_structure || profile?.office_space);
  const currentByType = useMemo(() => {
    const latest = {};
    taggedDocuments.forEach((doc) => {
      if (doc.is_current) latest[doc.document_type] = doc;
    });
    return latest;
  }, [taggedDocuments]);
  const missingTypes = useMemo(
    () => requirement.documents.filter((type) => !currentByType[type]),
    [requirement.documents, currentByType]
  );
  const showUpload = canUpdateKyc(roleFromProfile(profile)) && Boolean(profile) && missingTypes.length > 0;
  const canEditKyc = canUpdateKyc(roleFromProfile(profile));
  const replacing = Boolean(replaceDoc) && saving;
  const busy = saving;

  async function loadDocuments() {
    const payload = await agentApi.get("/travel-partner/kyc-documents");
    const data = payload?.data || payload || {};
    setDocuments(Array.isArray(data.documents) ? data.documents : Array.isArray(data) ? data : []);
  }

  async function refreshPage() {
    const current = await fetchCurrentAgent({ force: true });
    setProfile(current);
    await loadDocuments();
  }

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const current = await fetchCurrentAgent();
        if (!alive) return;
        setProfile(current);
        await loadDocuments();
      } catch (fetchError) {
        if (alive) showApiError(fetchError, "LOAD_AGENCY");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
      if (previewUrlRef.current) {
        window.URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = "";
      }
    };
  }, []);

  useEffect(() => {
    fetchCountries().then(setCountries).catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    const countryId = Number(profile?.country_id);
    if (!countryId) {
      setStates([]);
      return;
    }
    fetchStates(countryId).then(setStates).catch(() => setStates([]));
  }, [profile?.country_id]);

  useEffect(() => {
    const stateId = Number(profile?.state_id);
    if (!stateId) {
      setCities([]);
      return;
    }
    fetchCities(stateId).then(setCities).catch(() => setCities([]));
  }, [profile?.state_id]);

  useEffect(() => {
    if (!showUpload || missingTypes.length === 0) return;
    if (!missingTypes.includes(documentType)) {
      setDocumentType(missingTypes[0]);
    }
  }, [showUpload, missingTypes, documentType]);

  useEffect(() => {
    function warnOnLeave(event) {
      if (!saving) return;
      event.preventDefault();
      event.returnValue = "";
    }
    window.addEventListener("beforeunload", warnOnLeave);
    return () => window.removeEventListener("beforeunload", warnOnLeave);
  }, [saving]);

  function resetReplaceModal() {
    setReplaceDoc(null);
    setReplaceFile(null);
    setReplaceReason("");
    if (replaceInputRef.current) replaceInputRef.current.value = "";
  }

  function closeReplaceModal() {
    if (saving) return;
    resetReplaceModal();
  }

  function openReplaceModal(doc) {
    if (!canEditKyc || busy) return;
    if (isPendingReview(doc.review_status)) {
      showErrorAction("KYC_PENDING_REPLACE");
      return;
    }
    setReplaceDoc(doc);
    setReplaceFile(null);
    setReplaceReason("");
    if (replaceInputRef.current) replaceInputRef.current.value = "";
  }

  async function submitKycFile({ type, nextFile, reason, successMessage }) {
    if (!canEditKyc) return false;
    const invalid = validateKycFile(nextFile);
    if (invalid) {
      showError(invalid);
      return false;
    }
    const body = new FormData();
    body.append("documentType", type);
    body.append("document", nextFile);
    if (reason) body.append("reason", reason);
    await agentApi.post("/travel-partner/kyc-documents", { body });
    showSuccessAction(successMessage);
    await refreshPage();
    return true;
  }

  async function uploadDocument() {
    if (!canEditKyc || busy) return;
    try {
      setSaving(true);
      const uploaded = await submitKycFile({
        type: documentType,
        nextFile: file,
        successMessage: "UPLOAD_KYC",
      });
      if (uploaded) {
        setFile(null);
        if (uploadInputRef.current) uploadInputRef.current.value = "";
      }
    } catch (uploadError) {
      showApiError(uploadError, "UPLOAD_KYC");
    } finally {
      setSaving(false);
    }
  }

  async function submitReplacement() {
    if (!replaceDoc || busy) return;
    const currentOfType = currentByType[replaceDoc.document_type];
    if (currentOfType && isPendingReview(currentOfType.review_status)) {
      showErrorAction("KYC_PENDING_REPLACE");
      return;
    }
    try {
      setSaving(true);
      const uploaded = await submitKycFile({
        type: replaceDoc.document_type,
        nextFile: replaceFile,
        reason: replaceReason.trim(),
        successMessage: "REPLACE_KYC",
      });
      if (uploaded) resetReplaceModal();
    } catch (uploadError) {
      showApiError(uploadError, "REPLACE_KYC");
    } finally {
      setSaving(false);
    }
  }

  function closePreview() {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = "";
    }
    setPreview(null);
    setPreviewLoading(false);
  }

  async function previewDocument(doc) {
    if (!doc?.kyc_document_id) {
      showErrorAction("KYC_FILE_MISSING");
      return;
    }
    closePreview();
    setPreviewLoading(true);
    setPreview({
      title: typeLabel(doc.document_type),
      fileName: doc.original_file_name || "kyc-file",
      url: "",
      mimeType: "",
    });
    try {
      const blob = await agentApi.get(`/travel-partner/kyc-documents/${doc.kyc_document_id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setPreview({
        title: typeLabel(doc.document_type),
        fileName: doc.original_file_name || "kyc-file",
        url,
        mimeType: blob.type || "",
      });
    } catch (previewError) {
      closePreview();
      showApiError(previewError, "PREVIEW_KYC");
    } finally {
      setPreviewLoading(false);
    }
  }

  async function downloadDocument(doc) {
    if (!doc?.kyc_document_id) {
      showErrorAction("KYC_FILE_MISSING");
      return;
    }
    try {
      const blob = await agentApi.get(`/travel-partner/kyc-documents/${doc.kyc_document_id}/download`, {
        responseType: "blob",
      });
      downloadBlob(blob, doc.original_file_name || "kyc-file");
      showSuccessAction("DOWNLOAD_KYC");
    } catch (downloadError) {
      showApiError(downloadError, "DOWNLOAD_KYC");
    }
  }

  function rowAction(doc) {
    if (!canEditKyc) return null;
    if (!doc.is_current) return null;
    if (isPendingReview(doc.review_status)) return null;
    if (isRejectedReview(doc.review_status)) return "Resubmit";
    if (statusKey(doc.review_status) === "approved") return "Replace";
    return "Replace";
  }

  const uploadTypeOptions = KYC_TYPES.filter((item) => missingTypes.includes(item.value));
  const replaceMode = replaceDoc && isRejectedReview(replaceDoc.review_status) ? "Resubmit" : "Replace";
  const selectedCountry = findLocationLabel(countries, profile?.country_id, "country_id", "country_name");
  const selectedState = findLocationLabel(states, profile?.state_id, "state_id", "state_name");
  const selectedCity = findLocationLabel(cities, profile?.city_id, "city_id", "city_name");
  const address = [
    profile?.address_line1,
    profile?.address_line2,
    selectedCity,
    selectedState,
    selectedCountry,
    profile?.postal_code,
  ].filter(Boolean).join(", ");

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="KYC status"
          title={profile?.full_name || profile?.branch_name || "Agency details"}
          items={[DASHBOARD_CRUMB, { label: "KYC" }]}
          backTo="/dashboard"
          actions={
            canEditKyc ? (
              <Link to="/settings" className="btn btn-outline-primary btn-sm">Edit profile</Link>
            ) : null
          }
        />

        {loading ? <PageLoader message="Loading agency details..." /> : null}

        {!loading && profile ? (
          <div className="row g-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-24">
                  <div className="row g-4">
                    <div className="col-12 col-lg-4">
                      <DetailSection title="Verification">
                        <div className="col-12">
                          <div className="d-flex flex-wrap align-items-center gap-2 mb-8">
                            <StatusBadge value={profile.verification_status}>{copy.badge}</StatusBadge>
                            <StatusBadge value={profile.status} />
                          </div>
                        </div>
                        <div className="col-sm-6">
                          <DetailItem label="Registered" value={formatDate(profile.registration_date)} />
                        </div>
                        <div className="col-sm-6">
                          <DetailItem label="Last updated" value={formatDate(profile.last_updated)} />
                        </div>
                        <div className="col-12">
                          <p className="agent-kyc-note mb-0">{copy.body}</p>
                        </div>
                      </DetailSection>
                    </div>

                    <div className="col-12 col-lg-8">
                      <DetailSection title="Agency details">
                        <div className="col-sm-6">
                          <DetailItem label="Agency / branch" value={profile.branch_name} />
                        </div>
                        <div className="col-sm-6">
                          <DetailItem label="Contact" value={profile.full_name} />
                        </div>
                        <div className="col-sm-6">
                          <DetailItem label="Email" value={profile.email} />
                        </div>
                        <div className="col-sm-6">
                          <DetailItem label="Mobile" value={profile.mobile} />
                        </div>
                        <div className="col-sm-6">
                          <DetailItem label="GST" value={profile.gst_number} />
                        </div>
                        <div className="col-sm-6">
                          <DetailItem label="PAN" value={profile.agency_pan} />
                        </div>
                        <div className="col-sm-6">
                          <DetailItem label="Business type" value={profile.business_type} />
                        </div>
                        <div className="col-sm-6">
                          <DetailItem label="Country" value={selectedCountry} />
                        </div>
                        <div className="col-sm-6">
                          <DetailItem label="State" value={selectedState} />
                        </div>
                        <div className="col-sm-6">
                          <DetailItem label="City" value={selectedCity} />
                        </div>
                        <div className="col-12">
                          <DetailItem label="Address" value={address} />
                        </div>
                      </DetailSection>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <h5 className="mb-3">KYC files</h5>
                  {showUpload ? (
                    <div className="row g-3 align-items-end mb-4">
                      <div className="col-12 col-md-4">
                        <label className="form-label">Document type</label>
                        <AgentSelect
                          value={documentType}
                          options={uploadTypeOptions.length ? uploadTypeOptions : KYC_TYPES}
                          onChange={(value) => setDocumentType(value || missingTypes[0] || "pan_card")}
                          isSearchable={false}
                          isDisabled={busy}
                          placeholder="Select document type"
                        />
                      </div>
                      <div className="col-12 col-md-5">
                        <label className="form-label">File</label>
                        <input
                          ref={uploadInputRef}
                          type="file"
                          accept={KYC_ACCEPT}
                          className="form-control"
                          disabled={busy}
                          onChange={(event) => setFile(event.target.files?.[0] || null)}
                        />
                      </div>
                      <div className="col-12 col-md-3">
                        <button type="button" className="btn btn-primary w-100" disabled={busy} onClick={uploadDocument}>
                          {busy && !replaceDoc ? "Uploading..." : "Upload file"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="small text-secondary mb-3">
                      {canEditKyc
                        ? (isVerified
                          ? "Required documents are approved. Use Replace if a file needs to be updated for review."
                          : "Use Replace or Resubmit on a file below. Previous approved files stay on record.")
                        : "KYC files are managed by the Agency Admin. You can view and download them here."}
                    </p>
                  )}

                  <Table>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>File</th>
                        <th>Status</th>
                        <th>Notes</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taggedDocuments.length > 0 ? taggedDocuments.map((doc) => {
                        const action = rowAction(doc);
                        return (
                          <tr key={doc.kyc_document_id} className={doc.is_current ? "" : "text-secondary"}>
                            <td>
                              <div>{typeLabel(doc.document_type)}</div>
                              {doc.is_current ? null : <div className="small">Previous</div>}
                            </td>
                            <td>{doc.original_file_name || "Missing file"}</td>
                            <td>
                              <StatusBadge value={doc.review_status}>{reviewStatusLabel(doc.review_status)}</StatusBadge>
                            </td>
                            <td>{doc.review_notes || "—"}</td>
                            <td className="text-end">
                              <div className="agent-kyc-actions">
                                <button
                                  type="button"
                                  className="agent-icon-btn"
                                  title="Preview"
                                  disabled={busy}
                                  aria-label={`Preview ${typeLabel(doc.document_type)}`}
                                  onClick={() => previewDocument(doc)}
                                >
                                  <iconify-icon icon="solar:eye-outline"></iconify-icon>
                                </button>
                                <button
                                  type="button"
                                  className="agent-icon-btn"
                                  title="Download"
                                  disabled={busy}
                                  aria-label={`Download ${typeLabel(doc.document_type)}`}
                                  onClick={() => downloadDocument(doc)}
                                >
                                  <iconify-icon icon="solar:download-minimalistic-outline"></iconify-icon>
                                </button>
                                {action ? (
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0 agent-kyc-actions__link"
                                    disabled={busy}
                                    onClick={() => openReplaceModal(doc)}
                                  >
                                    {action}
                                  </button>
                                ) : (
                                  <span className="agent-kyc-actions__link agent-kyc-actions__link--spacer" aria-hidden="true">
                                    Resubmit
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      }) : (
                        <tr>
                          <td colSpan="5" className="text-secondary text-center py-4">No KYC files uploaded yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <Modal
        open={Boolean(preview) || previewLoading}
        title={preview?.title ? `${preview.title} preview` : "Document preview"}
        className="agent-document-preview-modal"
        dialogClassName="modal-dialog-scrollable"
        onClose={closePreview}
        footer={(
          <button type="button" className="btn btn-primary" onClick={closePreview}>
            Close
          </button>
        )}
      >
        {previewLoading || !preview?.url ? (
          <div className="text-center py-5">
            <div className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />
          </div>
        ) : (
          <div className="agent-document-preview">
            {isImageFile(preview.mimeType, preview.fileName) ? (
              <img src={preview.url} alt={preview.fileName} />
            ) : (
              <iframe title={preview.fileName} src={preview.url} />
            )}
            <div className="small text-secondary">{preview.fileName}</div>
          </div>
        )}
      </Modal>
      <Modal
        open={Boolean(replaceDoc)}
        title={`${replaceMode} ${replaceDoc ? typeLabel(replaceDoc.document_type) : "document"}`}
        dialogClassName="modal-dialog-scrollable"
        onClose={closeReplaceModal}
        footer={(
          <>
            <button type="button" className="btn btn-outline-secondary" disabled={replacing} onClick={closeReplaceModal}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" disabled={replacing} onClick={submitReplacement}>
              {replacing ? "Submitting..." : "Submit for Verification"}
            </button>
          </>
        )}
      >
        {replaceDoc ? (
          <div className="d-grid gap-3">
            <div>
              <div className="text-secondary small">Current document</div>
              <div className="fw-semibold">{replaceDoc.original_file_name || "Missing file"}</div>
            </div>
            <div>
              <div className="text-secondary small">Current status</div>
              <StatusBadge value={replaceDoc.review_status}>{reviewStatusLabel(replaceDoc.review_status)}</StatusBadge>
            </div>
            {replaceDoc.review_notes ? (
              <div>
                <div className="text-secondary small">Reviewer notes</div>
                <div>{replaceDoc.review_notes}</div>
              </div>
            ) : null}
            <div>
              <label className="form-label">Upload new document</label>
              <input
                ref={replaceInputRef}
                type="file"
                accept={KYC_ACCEPT}
                className="form-control"
                disabled={replacing}
                onChange={(event) => setReplaceFile(event.target.files?.[0] || null)}
              />
              <div className="small text-secondary mt-1">PDF, JPG, or PNG. Maximum 10 MB.</div>
            </div>
            <div>
              <label className="form-label">Reason for change <span className="text-secondary">(optional)</span></label>
              <textarea
                className="form-control"
                rows="3"
                maxLength={1000}
                disabled={replacing}
                value={replaceReason}
                onChange={(event) => setReplaceReason(event.target.value)}
                placeholder="Tell the reviewer why this file is being updated."
              />
            </div>
          </div>
        ) : null}
      </Modal>
    </AgentLayout>
  );
}
